import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import Button from '../ui/Button';
import { useCurrentUserProfile } from '../../contexts/CurrentUserProfileContext';
import { useMyRsvps } from '../../hooks/queries/useMyRsvps';
import { useFindGroups } from '../../hooks/queries/useFindGroups';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import {
  declineWelcome,
  finishWelcome,
  isWelcomeDemo,
  leaveWelcome,
  noteInvite,
  runWelcomeBeforeNext,
  shouldStartWelcome,
  startWelcome,
  wasInvited,
  welcomeStepFor,
  welcomeSteps,
} from '../../lib/welcomeDemo';

const BAR_HEIGHT_VAR = '--welcome-bar-h';

/**
 * The welcome flow's bar (Stage 10): under the header, how far along you are, one line, Skip and Next. It takes its own
 * room like the no-service banner - the page moves down by its height (--welcome-bar-h in --app-content-top) - so the
 * page's own top (search, chips) stays in view. The dock is hidden while it shows (Layout). From the second step a Back
 * returns to the step before. Invited, it first shows here as an offer on the page you joined from (Not now / Start).
 */
export default function WelcomeBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, authReady } = useCurrentUserProfile();
  const step = welcomeStepFor(location);
  const userId = session?.user?.id;
  const [offer, setOffer] = useState(false);
  const openOffer = useCallback(() => setOffer(true), []);
  useWelcomeTrigger(session?.user, authReady, location, navigate, openOffer);
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const showOffer = offer && !step;
  useLayoutEffect(() => {
    const root = document.documentElement;
    const h =
      (step || showOffer) && ref.current
        ? Math.round(ref.current.getBoundingClientRect().height) + 8
        : 0;
    root.style.setProperty(BAR_HEIGHT_VAR, `${h}px`);
    root.classList.toggle('welcome-demo', Boolean(step)); // no dock during the flow; the offer keeps it
    return () => {
      root.style.setProperty(BAR_HEIGHT_VAR, '0px');
      root.classList.remove('welcome-demo');
    };
  }, [step, showOffer]);
  const inFlow = Boolean(step);
  useEffect(() => {
    if (!inFlow) return undefined;
    let left = false;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        left = true;
        leaveWelcome(userId);
      } else if (left) finishWelcome(navigate, userId);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [inFlow, userId, navigate]);
  if (showOffer) {
    return (
      <div ref={ref} className="welcome-bar" role="region" aria-label="Getting started">
        <div className="welcome-bar__row">
          <p className="welcome-bar__line">Set up your profile</p>
          <button
            type="button"
            className="welcome-bar__skip"
            onClick={() => {
              declineWelcome(userId);
              setOffer(false);
            }}
          >
            Not now
          </button>
          <Button
            size="sm"
            className="!rounded-full !px-4"
            onClick={() => {
              setOffer(false);
              startWelcome(navigate, location.pathname + location.search);
            }}
          >
            Start
          </Button>
        </div>
      </div>
    );
  }
  const steps = welcomeSteps();
  const i = steps.findIndex((s) => s.key === step);
  if (i < 0) return null;
  const last = i === steps.length - 1;
  const next = async () => {
    if (busy) return;
    setBusy(true);
    const ok = await runWelcomeBeforeNext();
    setBusy(false);
    if (ok === false) return;
    if (last) finishWelcome(navigate, session?.user?.id);
    else navigate(steps[i + 1].path);
  };
  return (
    <div ref={ref} className="welcome-bar" role="region" aria-label="Getting started">
      <div className="welcome-bar__progress" aria-label={`Step ${i + 1} of ${steps.length}`}>
        {steps.map((s, n) => (
          <span
            key={s.key}
            className={n <= i ? 'welcome-bar__seg welcome-bar__seg--on' : 'welcome-bar__seg'}
          />
        ))}
      </div>
      <div className="welcome-bar__row">
        {i > 0 && (
          <button
            type="button"
            className="welcome-bar__back"
            aria-label="Back"
            onClick={() => navigate(steps[i - 1].path)}
          >
            <ChevronLeft size={18} aria-hidden="true" />
          </button>
        )}
        <p className="welcome-bar__line">{steps[i].line}</p>
        <button
          type="button"
          className="welcome-bar__skip"
          onClick={() => finishWelcome(navigate, session?.user?.id)}
        >
          Skip
        </button>
        <Button size="sm" className="!rounded-full !px-4" onClick={next} loading={busy}>
          {last ? 'Done' : 'Next'}
        </Button>
      </div>
    </div>
  );
}

/**
 * Starts the flow once for a brand-new account, the first time it is going to something or in a group (its first Join
 * or group join). A beat later, and never over an open sheet (the Group name step), so the join finishes first; Done or
 * Skip comes back to where you were. Invited, it never takes you off the group you came for: it offers instead.
 */
function useWelcomeTrigger(user, authReady, location, navigate, onOffer) {
  const eligible = shouldStartWelcome(user);
  const { data: rsvps = [] } = useMyRsvps(eligible ? user.id : null, {
    enabled: authReady && eligible,
  });
  const { data: groups = [] } = useFindGroups(eligible ? user.id : null, {
    authReady: authReady && eligible,
  });
  const joined = rsvps.some((r) => r.status === 'going') || groups.length > 0;
  // No service, no tour (Sam, 2026-10-31): its Sets and map are Portola's real data, loaded live, so it waits until the
  // phone is back online rather than showing empty steps. Bundling Portola into the app was not worth the download.
  const { isOnline, isEffectivelyUnreachable } = useNetworkStatus();
  const offline = !isOnline || isEffectivelyUnreachable;
  const here = location.pathname + location.search;
  // A group link seen on this phone marks whoever signs up from it as invited (wasInvited, a day).
  useEffect(() => {
    if (/[?&]invite=/.test(location.search) || /\/invite\//.test(location.pathname)) noteInvite();
  }, [location.pathname, location.search]);
  useEffect(() => {
    if (
      !eligible ||
      !joined ||
      offline ||
      isWelcomeDemo(location.search) ||
      location.pathname.startsWith('/login')
    )
      return undefined;
    const t = window.setTimeout(() => {
      if (document.querySelector('[role="dialog"]')) return; // try again on the next page
      if (wasInvited()) onOffer();
      else startWelcome(navigate, here);
    }, 1200);
    return () => window.clearTimeout(t);
  }, [eligible, joined, offline, here, location.pathname, location.search, navigate, onOffer]);
}
